import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getDraftFields } from "components/Beneficiar/utils";
import type { TTags } from "components/Title";
import type {
    IBeneficiar,
    IBeneficiaryHeir,
    ICreateSuccessionPayload,
    IRepresentative,
    ISuccessionHeirPayload,
    ISuccessionPreview,
} from "containers/Beneficiars/types";
import type { ITitle } from "containers/Titles/types";
import { toastApiError } from "helpers/apiErrorToast";
import { resolveCountryLanguage } from "helpers/countryLanguage";
import { base64ImageEncode, downloadApiExport, getDateForFileName, isFinalReq } from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import i18n, { t } from "i18next";
import { toast } from "react-toastify";

import { createAppAsyncThunk } from "./appAsyncThunk";
import type { IArticle } from "./articleStore";
import { type CreatedTermListEntry, insertCreatedTerm } from "./createdTerm";
import type { ITerm } from "./managementStore";
import type { SettingsState } from "./settingsStore";
import { setLocalSettings } from "./settingsStore";
import type { IBeneficiariesCompanies, TFetchStatus, TParamsPagination } from "./types";

export const fetchBeneficiaries = createAsyncThunk(
    "beneficiaries/getBeneficiaries",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/beneficiary", {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBeneficiariesAutocomplete = createAsyncThunk(
    "beneficiaries/getBeneficiariesAutocomplete",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/beneficiary", {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const createRepresentative = createAsyncThunk(
    "beneficiaries/createRepresentative",
    async (
        { beneficiaryId, data }: { beneficiaryId: number; data: Omit<IRepresentative, "id"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/representatives`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateRepresentative = createAsyncThunk(
    "beneficiaries/updateRepresentative",
    async (
        {
            beneficiaryId,
            representativeId,
            data,
        }: {
            beneficiaryId: number;
            representativeId: number;
            data: Omit<IRepresentative, "id">;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/beneficiary/${beneficiaryId}/representatives/${representativeId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchWithheldBeneficiaryPercentage = createAsyncThunk(
    "beneficiaries/withheldBeneficiaryPercentage",
    async (
        {
            beneficiaryId,
            periodId,
            percent,
        }: {
            beneficiaryId: number;
            periodId: number;
            percent: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/withheld`,
                {
                    periodId,
                    percent: Math.round(percent * 100),
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendPicture = createAsyncThunk("beneficiaries/sendPicture", async (file: File, { rejectWithValue }) => {
    try {
        const base64string = await base64ImageEncode(file);
        const payload = {
            name: file.name,
            mime: file.type,
            size: file.size,
            content: base64string,
        };
        const response = await axios.post<{ imagePath: string }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/images/upload-base64",
            payload,
        );
        return response.data.imagePath;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

type TCreateBeneficiary = any;

export const fetchCreateBeneficiary = createAsyncThunk(
    "beneficiaries/createBeneficiary",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/beneficiary", { data: data });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const updateBeneficiary = createAsyncThunk(
    "beneficiaries/updateBeneficiary",
    async ({ data, id }: { data: TCreateBeneficiary; id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchUpdateBeneficiaryPress = createAsyncThunk(
    "beneficiaries/updateBeneficiaryPress",
    async ({ data, id }: { data: IBeneficiar; id: number }, { rejectWithValue }) => {
        try {
            await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}`, data);
            return { data, id };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchUpdateBeneficaryFromPortal = createAsyncThunk(
    "beneficiaries/updateBeneficiaryFromPortal",
    async ({ data, companyId }: { data: TCreateBeneficiary; companyId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/beneficiary/profile`,
                data,
                { params: { companyId } },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchDeleteBeneficiary = createAsyncThunk(
    "beneficiaries/deleteBeneficiary",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const deleteRepresentative = createAsyncThunk(
    "beneficiaries/deleteRepresentative",
    async (
        { beneficiaryId, representativeId }: { beneficiaryId: number; representativeId: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/beneficiary/${beneficiaryId}/representatives/${representativeId}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchBeneficiar = createAsyncThunk(
    "beneficiaries/getBeneficiary",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}`);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAssociatedPersons = createAsyncThunk(
    "beneficiaries/getAssociatedPersons",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}/representatives`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchHeirs = createAsyncThunk("beneficiaries/getHeirs", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}/heirs`);
        return response.data.data as IBeneficiaryHeir[];
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchHeirOf = createAsyncThunk("beneficiaries/getHeirOf", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}/heir-of`);
        return response.data.data as IBeneficiaryHeir[];
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchSuccessionPreview = createAsyncThunk(
    "beneficiaries/getSuccessionPreview",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}/succession/preview`,
            );
            return response.data.data as ISuccessionPreview;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const createSuccession = createAsyncThunk(
    "beneficiaries/createSuccession",
    async ({ beneficiaryId, data }: { beneficiaryId: number; data: ICreateSuccessionPayload }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/succession`,
                data,
            );
            return response.data.data as IBeneficiaryHeir[];
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateSuccession = createAsyncThunk(
    "beneficiaries/updateSuccession",
    async (
        { beneficiaryId, heirs }: { beneficiaryId: number; heirs: ISuccessionHeirPayload[] },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/succession`,
                { heirs },
            );
            return response.data.data as IBeneficiaryHeir[];
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAssociatedTitles = createAsyncThunk(
    "beneficiaries/getAssociatedTitles",
    async (id: number, { rejectWithValue }) => {
        try {
            const fakeData = {
                photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTFhSnoly9J1ySaRA45scYB4Q1otdhWAXtLlg&usqp=CAU",
                associated: "Rose Marie",
                channels: [
                    {
                        photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTFhSnoly9J1ySaRA45scYB4Q1otdhWAXtLlg&usqp=CAU",
                    },
                    {
                        photo: "https://image.shutterstock.com/image-vector/people-icon-isolated-flat-design-260nw-401277397.jpg",
                    },
                ],
                end: "2022-31-12",
                balance: 100,
            };
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${id}/titles`);
            const fakeDataAppendedResponse = response.data.result.map((item: any) => ({ ...item, ...fakeData }));
            return fakeDataAppendedResponse;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBeneficiarPersonalInfo = createAsyncThunk(
    "beneficiaries/getBeneficiaryPersonalInfo",
    async ({ companyId }: { companyId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/beneficiary/profile`,
                { params: { companyId } },
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportBeneficiaryList = createAsyncThunk("beneficiaries/exportBeneficiaryList", async () => {
    const response = await toast.promise(
        axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/export`, { headers: { Accept: "*/*" } }),
        {
            pending: `${t("Export.Pending")}`,
            success: `${t("Export.Successful")}`,
            error: `${t("Export.Error")}`,
        },
    );

    const fileName = `export_beneficiars_${getDateForFileName()}.xlsx`;
    downloadApiExport(response.data, fileName);
});

export const fetchTermAssociatedToBeneficiaries = createAsyncThunk(
    "titles/fetchTermAssociatedToBeneficiaries",
    async ({ beneficiaryId }: { beneficiaryId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${beneficiaryId}/terms`,
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBeneficiariesNames = createAsyncThunk("beneficiaries/names", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/lightened`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const sendInvitation = createAsyncThunk(
    "beneficiaries/invite",
    async (data: { id: number; email: string; name: string; needsUpdate: boolean }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/beneficiary/${data.id}/invite`,
                data,
            );
            if (response.data.result) {
                return response.data.result;
            } else {
                throw Error;
            }
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getCompagnies = createAppAsyncThunk(
    "beneficiaries/compagnies",
    async (_, { rejectWithValue, getState, dispatch }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/beneficiary/companies`,
            );
            const companies = response.data?.data?.companies ?? [];
            if (companies.length) {
                const { language, languageSetByUser } = (getState() as any).settings?.accountSettings ?? {};
                const countryCode: string = companies[0]?.countryCode ?? "";
                const langPart = countryCode.split("-")[0]; // "fr" or "en"
                // A language chosen by the beneficiary is kept, but still mapped to its country bundle
                const desiredLocale = resolveCountryLanguage(
                    countryCode,
                    languageSetByUser && language ? language : langPart,
                );
                if (i18n.options.resources?.[desiredLocale]) {
                    i18n.changeLanguage(desiredLocale);
                    if (!languageSetByUser) {
                        dispatch(
                            setLocalSettings({
                                accountSettings: { language: langPart, languageSetByUser: false },
                            } as SettingsState),
                        );
                    }
                }
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getBeneficiaryTags = createAsyncThunk(
    "beneficiaries/getBeneficiaryTags",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/tags`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const createBeneficiaryTags = createAsyncThunk(
    "beneficiaries/createBeneficiaryTags",
    async (
        {
            tags,
            beneficiaryId,
            toast,
        }: {
            tags: { name: string; color: number } | { name: string; color: number }[];
            beneficiaryId: number;
            toast: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/tag`,
                {
                    tags,
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const editBeneficiaryTag = createAsyncThunk(
    "beneficiaries/editBeneficiaryTag",
    async (
        {
            tag,
            beneficiaryId,
            tagId,
        }: {
            tag: { name: string; color: number };
            beneficiaryId: number;
            tagId: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/tag/${tagId}`,
                tag,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteBeneficiaryTag = createAsyncThunk(
    "beneficiaries/deleteBeneficiaryTag",
    async (
        { beneficiaryId, tagId, toast }: { beneficiaryId: number; tagId: number; toast: boolean },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/tag/${tagId}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBeneficiaryStatements = createAsyncThunk(
    "beneficiaries/fetchBeneficiaryStatements",
    async ({ beneficiaryId }: { beneficiaryId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/statements`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Fetch list of existing term groups (advance compensation) linked to one beneficiary
export const fetchTermGroups = createAsyncThunk(
    "beneficiaries/fetchTermGroups",
    async ({ beneficiaryId }: { beneficiaryId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/groups`,
            );
            return response.data.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Creates a term group for a beneficiary
export const createTermGroup = createAsyncThunk(
    "beneficiaries/createTermGroup",
    async ({ beneficiaryId, name }: { beneficiaryId: number; name: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/groups`,
                { name },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Fetches one term group from beneficiary with its associated terms
export const fetchOneTermGroup = createAsyncThunk(
    "beneficiaries/fetchOneTermGroup",
    async ({ beneficiaryId, groupId }: { beneficiaryId: number; groupId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/groups/${groupId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Mark beneficiary profile as complete
 */
export const markBeneficiaryProfileComplete = createAsyncThunk(
    "beneficiaries/fetchUpdateIsProfileComplete",
    async (
        {
            beneficiaryId,
            isProfileComplete,
            toast,
        }: { beneficiaryId: number; isProfileComplete: boolean; toast: boolean },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/${beneficiaryId}/profile-complete`,
                { isProfileComplete },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type TEntity = "legal" | "physical" | "none";

export const getEntityNames = (t: (key: string) => string) => ({
    physical: t("LegalEntity.Physical"),
    legal: t("LegalEntity.Legal"),
});

export const entityList: TEntity[] = ["physical", "legal"];

export interface IStatement {
    id: number;
    termId: number | null;
    sliceId: number | null;
    avatarUrl: string;
    titleId: number | null;
    workId: number | null;
    name: string;
    grossDuty: number;
    netToPay: number;
    precount: "1" | "0";
    representative: number | null;
    periodId: number | null;
    periodStart: string | Date;
    periodEnd: string | Date;
    isPeriodActive: 0 | 1;
    type: "title" | "press" | "advance" | "consolidated";
}

export interface TermGroup {
    id: number;
    name: string;
    beneficiaryId: number;
    companyId: number;
    createdAt?: Date;
    modifiedAt?: Date;
    terms?: ITerm[];
    titles?: string[];
}

export interface BeneficiarState {
    updateBeneficiaryFromPortalStatus: TFetchStatus;
    createBeneficiaryStatus: TFetchStatus;
    updateBeneficiaryStatus: TFetchStatus;
    sendPictureStatus: TFetchStatus;
    deleteBeneficiaryStatus: TFetchStatus;
    deleteRepresentativeStatus: TFetchStatus;
    addRepresentativeStatus: TFetchStatus;
    editRepresentativeStatus: TFetchStatus;
    withheldPercentageStatus: TFetchStatus;
    beneficiaryStatus: "idle" | "pending" | "succeeded" | "failed";
    beneficiar: IBeneficiar;
    beneficiars: IBeneficiar[];
    beneficiarsAutocomplete: IBeneficiar[];
    countBeneficiars: number;
    associatedPersons: IRepresentative[];
    associatedTitles: ITitle[];
    associatedArticles: IArticle[];
    beneficiarPersonalInfo: IBeneficiar;
    associatedPersonsLoadStatus: TFetchStatus;
    associatedTitlesLoadStatus: TFetchStatus;
    associatedTitlesBeneficiaryId: number | null;
    associatedArticlesLoadStatus: TFetchStatus;
    beneficiarLoadStatus: TFetchStatus;
    fetchBeneficiarReqId: string;
    /** Latest `fetchBeneficiar` request. Stale detail responses must not overwrite the open fiche. */
    fetchBeneficiarDetailReqId: string;
    fetchBeneficiarDetailId: number | null;
    /** Latest beneficiary terms list request. A stale failure must not clear the table or toast. */
    fetchTermAssociatedReqId: string;
    isListRefreshing: boolean;
    termAssociatedToBeneficiaries: ITerm[];
    termAssociatedToBeneficiariesStatus: TFetchStatus;
    termAssociatedToBeneficiariesBeneficiaryId: number | null;
    beneficiarsNames: IBeneficiar[];
    beneficiarsNamesStatus: TFetchStatus;
    beneficiarCompagnies: IBeneficiariesCompanies[];
    beneficiarCompagniesStatus: TFetchStatus;
    crispHash: string;
    tags: TTags[] | null;
    lastAddedTagId: number;
    statusTags: TFetchStatus;
    statements: any;
    statementsStatus: TFetchStatus;
    termGroup: TermGroup;
    termGroups: TermGroup[];
    isInvited: boolean;
    missingData: string[];
    heirs: IBeneficiaryHeir[];
    heirsLoadStatus: TFetchStatus;
    heirsBeneficiaryId: number | null;
    heirOf: IBeneficiaryHeir[];
    heirOfLoadStatus: TFetchStatus;
    successionStatus: TFetchStatus;
}

const initialState: BeneficiarState = {
    updateBeneficiaryFromPortalStatus: "idle",
    updateBeneficiaryStatus: "idle",
    createBeneficiaryStatus: "idle",
    sendPictureStatus: "idle",
    deleteBeneficiaryStatus: "idle",
    withheldPercentageStatus: "idle",
    deleteRepresentativeStatus: "idle",
    addRepresentativeStatus: "idle",
    editRepresentativeStatus: "idle",
    beneficiaryStatus: "idle",
    beneficiar: {} as IBeneficiar,
    beneficiars: [],
    beneficiarsAutocomplete: [],
    countBeneficiars: 0,
    associatedPersons: [],
    associatedTitles: [],
    associatedArticles: [],
    beneficiarPersonalInfo: {} as IBeneficiar,
    associatedPersonsLoadStatus: "idle",
    associatedTitlesLoadStatus: "idle",
    associatedTitlesBeneficiaryId: null,
    associatedArticlesLoadStatus: "idle",
    beneficiarLoadStatus: "idle",
    fetchBeneficiarReqId: "",
    fetchBeneficiarDetailReqId: "",
    fetchBeneficiarDetailId: null,
    fetchTermAssociatedReqId: "",
    isListRefreshing: false,
    termAssociatedToBeneficiaries: [],
    termAssociatedToBeneficiariesStatus: "idle",
    termAssociatedToBeneficiariesBeneficiaryId: null,
    beneficiarsNames: [],
    beneficiarsNamesStatus: "idle",
    beneficiarCompagnies: [],
    beneficiarCompagniesStatus: "idle",
    crispHash: "",
    tags: null,
    lastAddedTagId: -1,
    statusTags: "idle",
    statements: [],
    statementsStatus: "idle",
    termGroup: {} as TermGroup,
    termGroups: [],
    isInvited: false,
    missingData: [],
    heirs: [],
    heirsLoadStatus: "idle",
    heirsBeneficiaryId: null,
    heirOf: [],
    heirOfLoadStatus: "idle",
    successionStatus: "idle",
};

export const beneficiarySlice = createSlice({
    name: "beneficiaries",
    initialState,
    reducers: {
        setAddRepresentativeIdleStatus(state) {
            state.addRepresentativeStatus = "idle";
        },
        setEditRepresentativeIdleStatus(state) {
            state.editRepresentativeStatus = "idle";
        },
        setDeleteRepresentativeStatus(state) {
            state.deleteRepresentativeStatus = "idle";
        },
        setDeleteBeneficiaryIdleStatus(state) {
            state.deleteBeneficiaryStatus = "idle";
        },
        deleteByTerm(state, action: PayloadAction<number>) {
            state.associatedTitles = state.associatedTitles.filter((title) => title?.termId !== action.payload);
            state.termAssociatedToBeneficiaries = state.termAssociatedToBeneficiaries.filter(
                (term) => term.id !== action.payload,
            );
        },
        addCreatedTerm(state, action: PayloadAction<CreatedTermListEntry>) {
            if (
                state.termAssociatedToBeneficiariesBeneficiaryId !== action.payload.beneficiarId ||
                state.termAssociatedToBeneficiariesStatus !== "succeeded"
            ) {
                return;
            }
            state.termAssociatedToBeneficiaries = insertCreatedTerm(
                state.termAssociatedToBeneficiaries,
                action.payload,
                {
                    avatar: "resource",
                    sortKey: (term) => term.isbn || term.title,
                },
            );
        },
        resetBeneficiarPersonalInfo(state) {
            state.beneficiarPersonalInfo = {} as IBeneficiar;
        },
        resetBeneficiaries(state) {
            // state.beneficiars = []
            state.beneficiar = {} as IBeneficiar;
            state.beneficiaryStatus = "idle";
            state.fetchBeneficiarDetailReqId = "";
            state.fetchBeneficiarDetailId = null;
        },
        prepareBeneficiarFetch(state) {
            state.beneficiaryStatus = "idle";
        },
        setBeneficiarPreview(state, action: PayloadAction<IBeneficiar>) {
            if (state.beneficiar?.id !== action.payload.id) {
                state.associatedTitles = [];
                state.associatedTitlesBeneficiaryId = null;
                state.associatedTitlesLoadStatus = "idle";
                state.termAssociatedToBeneficiaries = [];
                state.termAssociatedToBeneficiariesStatus = "idle";
                state.termAssociatedToBeneficiariesBeneficiaryId = null;
                state.fetchTermAssociatedReqId = "";
                state.beneficiaryStatus = "idle";
                // Drop an in-flight fiche fetch that targets someone else (list click ahead of the response).
                if (state.fetchBeneficiarDetailId !== action.payload.id) {
                    state.fetchBeneficiarDetailReqId = "";
                    state.fetchBeneficiarDetailId = null;
                }
            }
            state.beneficiar = action.payload;
        },
        resetBeneficiariesList(state) {
            state.beneficiars = [];
            state.countBeneficiars = 0;
            state.beneficiarLoadStatus = "idle";
            state.fetchBeneficiarReqId = "";
            state.isListRefreshing = false;
            state.associatedTitles = [];
            state.associatedTitlesBeneficiaryId = null;
            state.associatedTitlesLoadStatus = "idle";
            state.termAssociatedToBeneficiaries = [];
            state.termAssociatedToBeneficiariesStatus = "idle";
            state.termAssociatedToBeneficiariesBeneficiaryId = null;
            state.fetchTermAssociatedReqId = "";
        },
        addNoteToAssociatedTerm(state, action) {
            state.termAssociatedToBeneficiaries = state.termAssociatedToBeneficiaries.map((term) => {
                if (term.id === action.payload.termId) {
                    term.notesCount ? term.notesCount++ : (term.notesCount = 1);
                }
                return term;
            });
        },
        deleteNoteFromAssociatedTerm(state, action) {
            state.termAssociatedToBeneficiaries = state.termAssociatedToBeneficiaries.map((term) => {
                if (term.id === action.payload.termId) {
                    term.notesCount ? term.notesCount-- : (term.notesCount = 0);
                }
                return term;
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBeneficiaries.fulfilled, (state, action) => {
            const reqId = action.meta.requestId;
            if (!isFinalReq(state.fetchBeneficiarReqId, reqId)) return;
            state.beneficiarLoadStatus = "succeeded";
            state.isListRefreshing = false;
            if (action.payload.offset) {
                state.beneficiars = state.beneficiars.concat(action.payload.entries);
            } else {
                state.beneficiars = action.payload.entries;
            }
            state.countBeneficiars = +action.payload.count;
        });
        builder.addCase(fetchBeneficiaries.pending, (state, action) => {
            const reqId = action.meta.requestId;
            state.fetchBeneficiarReqId = reqId;
            state.beneficiarLoadStatus = "pending";
            if (!action.meta.arg?.offset) {
                state.isListRefreshing = true;
            }
        });
        builder.addCase(fetchBeneficiaries.rejected, (state, { payload, meta }) => {
            if (!isFinalReq(state.fetchBeneficiarReqId, meta.requestId)) return;
            state.beneficiarLoadStatus = "failed";
            state.isListRefreshing = false;
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchBeneficiariesAutocomplete.fulfilled, (state, action) => {
            if (action.payload.offset)
                state.beneficiarsAutocomplete = state.beneficiarsAutocomplete.concat(action.payload.entries);
            else state.beneficiarsAutocomplete = action.payload.entries;
        });
        builder.addCase(fetchBeneficiariesAutocomplete.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchWithheldBeneficiaryPercentage.pending, (state) => {
            state.withheldPercentageStatus = "pending";
        });
        builder.addCase(fetchWithheldBeneficiaryPercentage.fulfilled, (state, _action) => {
            state.withheldPercentageStatus = "succeeded";
            toast.success(`${t("Beneficiars.SuccessfulApplyWithheld")}`);
        });
        builder.addCase(fetchWithheldBeneficiaryPercentage.rejected, (state, { payload }) => {
            state.withheldPercentageStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchAssociatedPersons.fulfilled, (state, action) => {
            state.associatedPersonsLoadStatus = "succeeded";
            state.associatedPersons = action.payload;
        });
        builder.addCase(fetchAssociatedPersons.pending, (state, _action) => {
            state.associatedPersonsLoadStatus = "pending";
        });
        builder.addCase(fetchAssociatedPersons.rejected, (state, { payload }) => {
            state.associatedPersonsLoadStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchAssociatedTitles.fulfilled, (state, action) => {
            if (state.beneficiar?.id !== action.meta.arg) return;
            state.associatedTitlesLoadStatus = "succeeded";
            state.associatedTitles = action.payload;
            state.associatedTitlesBeneficiaryId = action.meta.arg;
        });
        builder.addCase(fetchAssociatedTitles.pending, (state, action) => {
            if (state.beneficiar?.id !== action.meta.arg) return;
            state.associatedTitlesLoadStatus = "pending";
            if (state.associatedTitlesBeneficiaryId !== action.meta.arg) {
                state.associatedTitles = [];
                state.associatedTitlesBeneficiaryId = action.meta.arg;
            }
        });
        builder.addCase(fetchAssociatedTitles.rejected, (state, { payload, meta }) => {
            if (state.beneficiar?.id !== meta.arg) return;
            state.associatedTitlesLoadStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchBeneficiarPersonalInfo.fulfilled, (state, action) => {
            const beneficiar = action.payload;
            const draftFields = getDraftFields(beneficiar);
            state.isInvited = !!draftFields.length;
            state.missingData = draftFields;
            state.beneficiarPersonalInfo = beneficiar;
        });
        builder.addCase(fetchBeneficiarPersonalInfo.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.beneficiarPersonalInfo = {} as IBeneficiar;
        });
        builder.addCase(fetchBeneficiar.fulfilled, (state, { payload, meta }) => {
            if (!isFinalReq(state.fetchBeneficiarDetailReqId, meta.requestId)) return;
            state.beneficiar = payload;
            state.beneficiaryStatus = "succeeded";
        });
        builder.addCase(fetchBeneficiar.pending, (state, { meta }) => {
            state.fetchBeneficiarDetailReqId = meta.requestId;
            state.fetchBeneficiarDetailId = meta.arg;
            // A refresh of the fiche already on screen must not flip it back to the skeleton.
            if (state.beneficiar?.id !== meta.arg || state.beneficiaryStatus !== "succeeded") {
                state.beneficiaryStatus = "pending";
            }
        });
        builder.addCase(fetchBeneficiar.rejected, (state, { payload, meta }) => {
            if (!isFinalReq(state.fetchBeneficiarDetailReqId, meta.requestId)) return;
            state.beneficiar = {} as IBeneficiar;
            state.beneficiaryStatus = "failed";
            state.fetchBeneficiarDetailId = null;
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(createRepresentative.fulfilled, (state, { payload }) => {
            state.associatedPersons = [...state.associatedPersons, payload];
            toast.success(`${t("Beneficiars.SuccessfulAddedRepresentative")}`);
            state.addRepresentativeStatus = "succeeded";
        });
        builder.addCase(createRepresentative.pending, (state) => {
            state.addRepresentativeStatus = "pending";
        });
        builder.addCase(createRepresentative.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.addRepresentativeStatus = "failed";
        });

        builder.addCase(updateRepresentative.fulfilled, (state, { payload }) => {
            state.associatedPersons = [...state.associatedPersons].map((item) => {
                if (item.id === payload.id) {
                    return payload;
                } else {
                    return item;
                }
            });
            toast.success(`${t("Beneficiars.SuccessfulEditRepresentative")}`);
            state.editRepresentativeStatus = "succeeded";
        });
        builder.addCase(updateRepresentative.pending, (state) => {
            state.editRepresentativeStatus = "pending";
        });
        builder.addCase(updateRepresentative.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.editRepresentativeStatus = "failed";
        });

        builder.addCase(deleteRepresentative.fulfilled, (state, action) => {
            toast.success(`${t("Beneficiars.SuccessfullDeleteRepresentative")}`);
            state.associatedPersons = [...state.associatedPersons].filter(
                (assoP) => assoP.id !== action.meta.arg.representativeId,
            );
            state.deleteRepresentativeStatus = "succeeded";
        });
        builder.addCase(deleteRepresentative.pending, (state) => {
            state.deleteRepresentativeStatus = "pending";
        });
        builder.addCase(deleteRepresentative.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.deleteRepresentativeStatus = "failed";
        });

        builder.addCase(fetchDeleteBeneficiary.fulfilled, (state, action) => {
            toast.success(`${t("Beneficiars.SuccessfullyDeleted")}`);
            state.deleteBeneficiaryStatus = "succeeded";
            state.beneficiars = state.beneficiars.filter((benef) => benef.id !== action.meta.arg);
        });
        builder.addCase(fetchDeleteBeneficiary.pending, (state) => {
            state.deleteBeneficiaryStatus = "pending";
        });
        builder.addCase(fetchDeleteBeneficiary.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.deleteBeneficiaryStatus = "failed";
        });

        builder.addCase(fetchCreateBeneficiary.fulfilled, (state, action) => {
            toast.success(`${t("NewBeneficiary.SuccessfullAddBeneficiary")}`);
            state.createBeneficiaryStatus = "succeeded";
            const newBenef = {
                id: action.payload.data.id,
                ...action.meta.arg,
            };
            state.beneficiars = [newBenef, ...state.beneficiars];
            state.beneficiarsAutocomplete = [newBenef, ...state.beneficiarsAutocomplete];
        });
        builder.addCase(fetchCreateBeneficiary.pending, (state) => {
            state.createBeneficiaryStatus = "pending";
        });
        builder.addCase(fetchCreateBeneficiary.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.createBeneficiaryStatus = "failed";
        });

        builder.addCase(updateBeneficiary.fulfilled, (state, action) => {
            const reqData = action.payload.result;
            toast.success(`${t("NewBeneficiary.SuccessfullUpdateInfo")}`);
            state.updateBeneficiaryStatus = "succeeded";
            state.beneficiars = state.beneficiars.map((beneficiar) => {
                if (beneficiar.id == reqData.id) {
                    beneficiar.avatarUrl = reqData.avatarUrl ?? beneficiar.avatarUrl;
                    beneficiar.name = reqData.name ?? beneficiar.name;
                    beneficiar.surname = reqData.surname ?? beneficiar.surname;
                }
                return beneficiar;
            });
            state.beneficiar = { ...state.beneficiar, ...reqData };
        });
        builder.addCase(updateBeneficiary.pending, (state) => {
            state.updateBeneficiaryStatus = "pending";
        });
        builder.addCase(updateBeneficiary.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.updateBeneficiaryStatus = "failed";
        });

        builder.addCase(fetchUpdateBeneficiaryPress.fulfilled, (state, action) => {
            toast.success(`${t("NewBeneficiary.SuccessfullUpdateInfo")}`);
            state.updateBeneficiaryStatus = "succeeded";
            state.beneficiars = state.beneficiars.map((benef) => {
                if (benef.id == action.payload.id) {
                    benef.avatarUrl = action.payload.data.avatarUrl ?? benef.avatarUrl;
                    benef.name = action.payload.data.name ?? benef.name;
                    benef.surname = action.payload.data.surname ?? benef.surname;
                }
                return benef;
            });
            state.beneficiar = { ...state.beneficiar, ...action.payload.data };
        });
        builder.addCase(fetchUpdateBeneficiaryPress.pending, (state) => {
            state.updateBeneficiaryStatus = "pending";
        });
        builder.addCase(fetchUpdateBeneficiaryPress.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.updateBeneficiaryStatus = "failed";
        });

        builder.addCase(fetchUpdateBeneficaryFromPortal.fulfilled, (state, action) => {
            const response = action.meta.arg.data;
            toast.success(`${t("NewBeneficiary.SuccessfullUpdateInfo")}`);
            const beneficiar = action.payload.result[0]; // main portal
            const draftFields = getDraftFields(beneficiar);
            state.missingData = draftFields;
            state.isInvited = !!draftFields.length;
            state.beneficiarPersonalInfo = {
                ...state.beneficiarPersonalInfo,
                ...response,
                customFields: state.beneficiarPersonalInfo.customFields,
            };
            state.updateBeneficiaryFromPortalStatus = "succeeded";
        });
        builder.addCase(fetchUpdateBeneficaryFromPortal.pending, (state) => {
            state.updateBeneficiaryFromPortalStatus = "pending";
        });
        builder.addCase(fetchUpdateBeneficaryFromPortal.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.updateBeneficiaryFromPortalStatus = "failed";
        });

        builder.addCase(sendPicture.fulfilled, (state) => {
            toast.success(`${t("Beneficiars.AddPictureSuccess")}`);
            state.sendPictureStatus = "succeeded";
        });
        builder.addCase(sendPicture.pending, (state) => {
            state.sendPictureStatus = "pending";
        });
        builder.addCase(sendPicture.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.sendPictureStatus = "failed";
        });

        builder.addCase(fetchTermAssociatedToBeneficiaries.pending, (state, action) => {
            state.fetchTermAssociatedReqId = action.meta.requestId;
            if (state.beneficiar?.id !== action.meta.arg.beneficiaryId) return;
            state.termAssociatedToBeneficiariesStatus = "pending";
            if (state.termAssociatedToBeneficiariesBeneficiaryId !== action.meta.arg.beneficiaryId) {
                state.termAssociatedToBeneficiaries = [];
                state.termAssociatedToBeneficiariesBeneficiaryId = action.meta.arg.beneficiaryId;
            }
        });
        builder.addCase(fetchTermAssociatedToBeneficiaries.fulfilled, (state, action) => {
            if (!isFinalReq(state.fetchTermAssociatedReqId, action.meta.requestId)) return;
            if (state.beneficiar?.id !== action.meta.arg.beneficiaryId) return;
            state.termAssociatedToBeneficiariesStatus = "succeeded";
            state.termAssociatedToBeneficiaries = action.payload;
            state.termAssociatedToBeneficiariesBeneficiaryId = action.meta.arg.beneficiaryId;
        });
        builder.addCase(fetchTermAssociatedToBeneficiaries.rejected, (state, { payload, meta }) => {
            if (!isFinalReq(state.fetchTermAssociatedReqId, meta.requestId)) return;
            if (state.beneficiar?.id !== meta.arg.beneficiaryId) return;
            state.termAssociatedToBeneficiariesStatus = "failed";
            state.termAssociatedToBeneficiaries = [];
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchBeneficiariesNames.pending, (state) => {
            state.beneficiarsNamesStatus = "pending";
        });
        builder.addCase(fetchBeneficiariesNames.fulfilled, (state, action) => {
            state.beneficiarsNames = action.payload.entries;
            state.beneficiarsNamesStatus = "succeeded";
        });
        builder.addCase(fetchBeneficiariesNames.rejected, (state, { payload }) => {
            state.beneficiarsNamesStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(sendInvitation.pending, () => {});
        builder.addCase(sendInvitation.fulfilled, (state, action) => {
            toast.success(`${t("Beneficiars.UserInvite")}`);
            const currentBeneficiar = JSON.parse(JSON.stringify(state.beneficiar));
            state.beneficiar.accountStatus = "pending";
            if (!currentBeneficiar.email) state.beneficiar.email = action.payload.cognitoUserData.email;
            state.beneficiars = state.beneficiars.map((benef) => {
                if (state.beneficiar?.id === benef.id) {
                    benef.accountStatus = "pending";
                }
                return benef;
            });
        });
        builder.addCase(sendInvitation.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(getCompagnies.pending, () => {});
        builder.addCase(getCompagnies.fulfilled, (state, action) => {
            state.beneficiarCompagnies = action.payload?.data?.companies ?? [];
            state.crispHash = action.payload?.data?.hash;
            state.beneficiarCompagniesStatus = "succeeded";
        });
        builder.addCase(getCompagnies.rejected, (state, { payload }) => {
            state.beneficiarCompagnies = [];
            state.beneficiarCompagniesStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(getBeneficiaryTags.fulfilled, (state, action) => {
            state.tags = action.payload.data;
            state.statusTags = "succeeded";
        });
        builder.addCase(getBeneficiaryTags.rejected, (state, { payload }) => {
            state.tags = [];
            state.statusTags = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(createBeneficiaryTags.fulfilled, (state, action) => {
            const { beneficiaryId, toast: showToast } = action.meta.arg;
            if (!beneficiaryId) return;

            const newTag = action.payload.data;
            const currentBeneficiaries = state.beneficiars.map((b) => {
                if (b.id === beneficiaryId) {
                    const updatedTags = b.tags ? [...b.tags, ...newTag] : [newTag];
                    return { ...b, tags: updatedTags };
                }
                return b;
            });

            state.beneficiars = currentBeneficiaries;
            state.beneficiar =
                currentBeneficiaries.find((beneficiary) => beneficiary.id === beneficiaryId) || state.beneficiar;

            const sameTagExist = state.tags?.some((tag) => tag?.name === newTag[0]?.name);
            if (!sameTagExist) {
                state.tags = state.tags ? [...state.tags, ...newTag] : [newTag];
                state.lastAddedTagId = newTag[0].id;
            }
            if (showToast) {
                toast.success(`${t("TagsFilter.CreateTagSuccess")}`);
            }
        });
        builder.addCase(createBeneficiaryTags.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(editBeneficiaryTag.fulfilled, () => {
            // TODO => Make component to edit existing tag
            toast.success(`${t("TagsFilter.EditTagSuccess")}`);
        });
        builder.addCase(editBeneficiaryTag.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(deleteBeneficiaryTag.fulfilled, (state, action) => {
            const { beneficiaryId, tagId, toast: showToast } = action.meta.arg;
            if (!beneficiaryId) return;

            const updatedBeneficiaries = state.beneficiars.map((b) => {
                if (b.id === beneficiaryId && b.tags) {
                    return {
                        ...b,
                        tags: b.tags.filter((tag) => tag.id !== tagId),
                    };
                }
                return b;
            });

            state.beneficiars = updatedBeneficiaries;
            state.beneficiar =
                updatedBeneficiaries.find((beneficiary) => beneficiary.id === beneficiaryId) || state.beneficiar;

            if (showToast) {
                toast.success(`${t("TagsFilter.DeleteTagSuccess")}`);
            }
        });
        builder.addCase(deleteBeneficiaryTag.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // Fetch statements
        builder.addCase(fetchBeneficiaryStatements.fulfilled, (state, { payload }) => {
            state.statementsStatus = "succeeded";
            state.statements = payload;
        });
        builder.addCase(fetchBeneficiaryStatements.pending, (state) => {
            state.statementsStatus = "pending";
        });
        builder.addCase(fetchBeneficiaryStatements.rejected, (state, { payload }) => {
            state.statementsStatus = "failed";
            state.statements = [];
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // Fetch term groups for one beneficiary
        builder.addCase(fetchTermGroups.fulfilled, (state, action) => {
            state.termGroups = action.payload;
        });
        builder.addCase(fetchTermGroups.pending, (state) => {
            state.termGroups = [];
        });
        builder.addCase(fetchTermGroups.rejected, (_, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // Creates a term group for one beneficiary
        builder.addCase(createTermGroup.fulfilled, (state, action) => {
            if (action.payload) state.termGroups.push(action.payload);
            toast.success(`${t("CompensationGroups.TermGroupCreated")}`);
        });
        builder.addCase(createTermGroup.rejected, (_, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // Fetches one term group with its associated terms
        builder.addCase(fetchOneTermGroup.fulfilled, (state, action) => {
            state.termGroup = action.payload;
        });
        builder.addCase(fetchOneTermGroup.rejected, (_, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(markBeneficiaryProfileComplete.fulfilled, (state, action) => {
            const { isProfileComplete } = action.payload;
            const { beneficiaryId, toast: sendToast } = action.meta.arg;
            state.beneficiar = { ...state.beneficiar, isProfileComplete };
            state.beneficiars = state.beneficiars.map((b) => {
                if (b.id == beneficiaryId) {
                    return { ...b, isProfileComplete };
                }
                return b;
            });
            if (sendToast) {
                toast.success(`${t("Beneficiars.ProfileCompleteSuccess")}`);
            }
        });
        builder.addCase(markBeneficiaryProfileComplete.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchHeirs.pending, (state, action) => {
            const sameBeneficiary = state.heirsBeneficiaryId === action.meta.arg;
            if (sameBeneficiary && state.heirsLoadStatus === "succeeded") return;
            if (!sameBeneficiary) {
                state.heirs = [];
                state.heirOf = [];
                state.heirOfLoadStatus = "pending";
                state.heirsBeneficiaryId = action.meta.arg;
            }
            state.heirsLoadStatus = "pending";
        });
        builder.addCase(fetchHeirs.fulfilled, (state, { payload, meta }) => {
            if (state.heirsBeneficiaryId !== meta.arg) return;
            state.heirs = payload;
            state.heirsLoadStatus = "succeeded";
        });
        builder.addCase(fetchHeirs.rejected, (state, { meta }) => {
            if (state.heirsBeneficiaryId !== meta.arg) return;
            state.heirs = [];
            state.heirsLoadStatus = "failed";
        });
        builder.addCase(fetchHeirOf.pending, (state, action) => {
            const sameBeneficiary = state.heirsBeneficiaryId === action.meta.arg;
            if (sameBeneficiary && state.heirOfLoadStatus === "succeeded") return;
            if (!sameBeneficiary) {
                state.heirOf = [];
                state.heirsBeneficiaryId = action.meta.arg;
            }
            state.heirOfLoadStatus = "pending";
        });
        builder.addCase(fetchHeirOf.fulfilled, (state, { payload, meta }) => {
            if (state.heirsBeneficiaryId !== meta.arg) return;
            state.heirOf = payload;
            state.heirOfLoadStatus = "succeeded";
        });
        builder.addCase(fetchHeirOf.rejected, (state, { meta }) => {
            if (state.heirsBeneficiaryId !== meta.arg) return;
            state.heirOf = [];
            state.heirOfLoadStatus = "failed";
        });
        builder.addCase(createSuccession.pending, (state) => {
            state.successionStatus = "pending";
        });
        builder.addCase(createSuccession.fulfilled, (state, { payload }) => {
            state.heirs = payload;
            state.successionStatus = "succeeded";
            toast.success(`${t("Heirs.Modal.Success")}`);
        });
        builder.addCase(createSuccession.rejected, (state, { payload }) => {
            state.successionStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "succession-error" });
        });
        builder.addCase(updateSuccession.pending, (state) => {
            state.successionStatus = "pending";
        });
        builder.addCase(updateSuccession.fulfilled, (state, { payload }) => {
            state.heirs = payload;
            state.successionStatus = "succeeded";
            toast.success(`${t("Heirs.Modal.SuccessUpdate")}`);
        });
        builder.addCase(updateSuccession.rejected, (state, { payload }) => {
            state.successionStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "succession-error" });
        });
    },
});
export const {
    setAddRepresentativeIdleStatus,
    setEditRepresentativeIdleStatus,
    setDeleteRepresentativeStatus,
    setDeleteBeneficiaryIdleStatus,
    deleteByTerm,
    addCreatedTerm,
    resetBeneficiarPersonalInfo,
    resetBeneficiaries,
    prepareBeneficiarFetch,
    setBeneficiarPreview,
    resetBeneficiariesList,
    addNoteToAssociatedTerm,
    deleteNoteFromAssociatedTerm,
} = beneficiarySlice.actions;

export default beneficiarySlice.reducer;
